<template>
  <div>
    <b-row class="heading-container">
      <go-back />
      <h4 class="ml-2">
        {{ $t("closeout_details") | capitalize }}
      </h4>
    </b-row>
    <modal
      id="snapshot-export-modal"
      :customTitle="$t('export_snapshot_list') | capitalize"
      modal-class="custom-modal edit"
      modalType="edit"
      size="l"
      centered
      hide-footer>
        <snapshot-export-form/>
    </modal>
    <validation-observer ref="observer" v-slot="{ invalid }">
      <div class="d-flex closeout-sections">
        <labeled-wrapper :label="$t('general') | capitalize">
          <b-col>
            <b-row>
              <distribution-detail-field
                labelCols="6"
                valueCols="6"
                :label="$t('tariff_period') | capitalize"
                :value="distribution.description"
              />
            </b-row>
            <b-row>
              <distribution-detail-field
                labelCols="6"
                valueCols="6"
                :label="$t('creator') | capitalize"
                :value="closeout.createdByName"
              >
                <template v-slot:input="{ state }">
                  <b-form-input
                    disabled
                    v-model="closeout.createdByName"
                    :state="state"
                  />
                </template>
              </distribution-detail-field>
            </b-row>
            <b-row>
              <distribution-detail-field
                labelCols="6"
                valueCols="6"
                :label="$t('college') | capitalize"
                :value="$t(closeout.college) | capitalize"
              />
            </b-row>
            <b-row>
              <distribution-detail-field
                labelCols="6"
                valueCols="6"
                :label="$t('created_on') | capitalize"
                :value="parseDate(closeout.createdAt, $config.DATE_FORMAT)"
              />
            </b-row>
          </b-col>
        </labeled-wrapper>
        <labeled-wrapper :label="$t('revenue') | capitalize">
          <b-col>
            <b-row>
              <distribution-detail-field
                labelCols="6"
                valueCols="6"
                :label="`${$t('remaining_revenue')}` | capitalize"
              >
                <template v-slot:value v-if="closeout.totalCloseoutRevenue">
                  ${{ closeout.totalCloseoutRevenue | formatNumber(2) }}
                </template>
              </distribution-detail-field>
            </b-row>
            <b-row>
              <distribution-detail-field
                labelCols="6"
                valueCols="6"
                :label="`${$t('remaining_revenue_override')}` | capitalize"
                vid="total_closeout_revenue_override"
                ref="total_closeout_revenue_override"
                :edit-mode="editMode"
                rules="double|positive"
              >
                <template
                  v-slot:value
                  v-if="closeout.totalCloseoutRevenueOverride"
                >
                  ${{ closeout.totalCloseoutRevenueOverride | formatNumber(2,2) }}
                </template>
                <template v-slot:input="{ state }">
                  <b-form-input
                    v-model="closeout.totalCloseoutRevenueOverride"
                    :state="state"
                    type="number"
                    min="0.01"
                    step="0.01"
                  ></b-form-input>
                </template>
              </distribution-detail-field>
            </b-row>
            <b-row>
              <distribution-detail-field
                v-if="!editMode"
                labelCols="6"
                valueCols="6"
                :label="getReserveAmountLabel({
                  reserveNumber: 1,
                  type: 'percent',
                })"
                :value="getViewReserveValue({
                  reserveNumber: 1,
                  type: 'percent',
                })"
              />
              <distribution-detail-field
                v-if="!editMode"
                labelCols="6"
                valueCols="6"
                :label="getReserveAmountLabel({
                  reserveNumber: 1,
                  type: 'amount',
                })"
                :value="getViewReserveValue({
                  reserveNumber: 1,
                  type: 'amount',
                })"
              />
              <distribution-detail-field
                v-if="editMode"
                labelCols="6"
                valueCols="6"
                :label="
                  getReserveAmountLabel({
                    reserveNumber: 1,
                    type: closeout.reserve1ValueType,
                  })
                "
                vid="reserve1_value"
                ref="reserve1_value"
                edit-mode
                rules="double|positive"
              >
                <template v-slot:value v-if="closeout.reserve1Value">
                  {{
                    getReserveValue(
                      closeout.reserve1Value,
                      closeout.reserve1ValueType,
                    )
                  }}
                </template>
                <template v-slot:input="{ state, validate }">
                  <b-input-group>
                    <template v-slot:append>
                      <b-form-select
                        :options="reserveTypeOptions"
                        v-model="closeout.reserve1ValueType"
                        @change="validate(closeout.reserve1Value)"
                      />
                    </template>
                    <b-form-input
                      v-model="closeout.reserve1Value"
                      :state="state"
                      @input="validate"
                    ></b-form-input>
                  </b-input-group>
                </template>
              </distribution-detail-field>
            </b-row>
            <b-row>
              <distribution-detail-field
                v-if="closeout.reserve1Note || editMode"
                labelCols="6"
                valueCols="6"
                :label="getReserveNotesLabel({ reserveNumber: 1 })"
                vid="reserve1_notes"
                ref="reserve1_notes"
                :edit-mode="editMode"
              >
                <template v-slot:value v-if="closeout.reserve1Note">
                  {{ closeout.reserve1Note }}
                </template>
                <template v-slot:input="{ state }">
                  <b-form-textarea
                    v-model="closeout.reserve1Note"
                    :state="state"
                  />
                </template>
              </distribution-detail-field>
            </b-row>
            <b-row>
              <distribution-detail-field
                v-if="!editMode && closeout.reserve2Value"
                labelCols="6"
                valueCols="6"
                :label="getReserveAmountLabel({
                  reserveNumber: 2,
                  type: 'percent',
                })"
                :value="getViewReserveValue({
                  reserveNumber: 2,
                  type: 'percent',
                })"
              />
              <distribution-detail-field
                v-if="!editMode && closeout.reserve2Value"
                labelCols="6"
                valueCols="6"
                :label="getReserveAmountLabel({
                  reserveNumber: 2,
                  type: 'amount',
                })"
                :value="getViewReserveValue({
                  reserveNumber: 2,
                  type: 'amount',
                })"
              />
              <distribution-detail-field
                v-if="editMode"
                labelCols="6"
                valueCols="6"
                :label="
                  getReserveAmountLabel({
                    reserveNumber: 2,
                    type: closeout.reserve2ValueType,
                  })
                "
                vid="reserve2_value"
                ref="reserve2_value"
                :edit-mode="editMode"
                rules="double|positive"
              >
                <template v-slot:value v-if="closeout.reserve2Value">
                  {{
                    getReserveValue(
                      closeout.reserve2Value,
                      closeout.reserve2ValueType,
                    )
                  }}
                </template>
                <template v-slot:input="{ state, validate }">
                  <b-input-group>
                    <template v-slot:append>
                      <b-form-select
                        :options="reserveTypeOptions"
                        v-model="closeout.reserve2ValueType"
                        @change="validate(closeout.reserve2Value)"
                      />
                    </template>
                    <b-form-input
                      v-model="closeout.reserve2Value"
                      :state="state"
                      @input="validate"
                    ></b-form-input>
                  </b-input-group>
                </template>
              </distribution-detail-field>
            </b-row>
            <b-row>
              <distribution-detail-field
                v-if="(closeout.reserve2Value && closeout.reserve2Note) || editMode"
                labelCols="6"
                valueCols="6"
                :label="getReserveNotesLabel({ reserveNumber: 2 })"
                vid="reserve2_notes"
                ref="reserve2_notes"
                :edit-mode="editMode"
              >
                <template v-slot:value v-if="closeout.reserve2Note">
                  {{ closeout.reserve2Note }}
                </template>
                <template v-slot:input="{ state }">
                  <b-form-textarea
                    v-model="closeout.reserve2Note"
                    :state="state"
                  />
                </template>
              </distribution-detail-field>
            </b-row>
            <b-row>
              <distribution-detail-field
                labelCols="6"
                valueCols="6"
                :label="`${$t('net_available_for_closeout')}` | capitalize"
              >
                <template v-slot:value>
                  ${{ closeout.remainingCloseoutRevenue | formatNumber(2,2) }}
                </template>
              </distribution-detail-field>
            </b-row>
          </b-col>
        </labeled-wrapper>
        <labeled-wrapper :label="$t('payment_history_snapshot')">
          <b-col>
            <b-row>
              <distribution-detail-field
                labelCols="6"
                valueCols="6"
                :label="`${$t('created_on')}` | capitalize"
              >
                <template v-slot:value v-if="closeout.snapshotCreated">
                  {{ parseDate(closeout.snapshotCreated, $config.DATE_FORMAT) }}
                </template>
              </distribution-detail-field>
            </b-row>
            <b-row>
              <distribution-detail-field
                labelCols="6"
                valueCols="6"
                :label="`${$t('total_transactions')}` | capitalize"
                :value="getSnapshotRowsStat('totalTransactions')"
              />
            </b-row>
            <b-row>
              <distribution-detail-field
                labelCols="6"
                valueCols="6"
                :label="`${$t('total_royalties_paid')}` | capitalize"
              >
                <template
                  v-slot:value
                  v-if="
                    closeout.originalRoyaltiesPaid && closeout.snapshotCreated
                  "
                >
                  ${{ closeout.originalRoyaltiesPaid | formatNumber(2,2) }}
                </template>
              </distribution-detail-field>
            </b-row>
            <b-row>
              <distribution-detail-field
                labelCols="6"
                valueCols="6"
                :label="`${$t('total_usage_paid')}` | capitalize"
              >
                <template
                  v-slot:value
                  v-if="closeout.snapshotCreated"
                >
                  {{ closeout.originalUsagesPaid | formatNumber(2,2) }}
                </template>
              </distribution-detail-field>
            </b-row>
            <b-row>
              <distribution-detail-field
                labelCols="6"
                valueCols="6"
                :label="`${$t('transactions_removed')}` | capitalize"
                :value="getSnapshotRowsStat('removedTransactions')"
              />
            </b-row>
            <b-row>
              <distribution-detail-field
                labelCols="6"
                valueCols="6"
                :label="`${$t('remaining_transactions')}` | capitalize"
                :value="getSnapshotRowsStat('remainTransactions')"
              />
            </b-row>
          </b-col>
        </labeled-wrapper>
        <labeled-wrapper :label="$t('vpp_and_topup_payments')">
          <b-col>
            <b-row>
              <distribution-detail-field
                labelCols="6"
                valueCols="6"
                :label="`${$t('total_usage_for_vpp')}`"
              >
              <template
                  v-slot:value
                  v-if="closeout.snapshotCreated"
                >
                  {{ closeout.currentUsagesPaid | formatNumber(2,2) }}
                </template>
              </distribution-detail-field>
            </b-row>
            <b-row>
              <distribution-detail-field
                labelCols="6"
                valueCols="6"
                :label="`${$t('closeout_vpp')}`"
                :value="closeout.snapshotCreated ? closeout.vpp : '-'"
              >
              </distribution-detail-field>
            </b-row>
            <b-row>
              <distribution-detail-field
                labelCols="6"
                valueCols="6"
                vid="vpp_override"
                ref="vpp_override"
                :label="`${$t('vpp_override')}` | capitalize"
                :edit-mode="editMode"
                :rules="`double|positive|max_decimals_8|vppOverride:${closeout.vpp}`"
                >
                <template v-slot:value v-if="closeout.vppOverride">
                  ${{ closeout.vppOverride | formatNumber(2) }}
                </template>
                <template v-slot:input="{ state }">
                  <b-form-input
                    v-model="closeout.vppOverride"
                    :state="state"
                    :label="$t('vpp_override') | capitalize"
                    type="number"
                    min="0"
                    step="0.01"
                  ></b-form-input>
                </template>
              </distribution-detail-field>
            </b-row>
            <b-row v-if="!!closeout.vppOverride">
              <distribution-detail-field
                  labelCols="6"
                  valueCols="6"
                  :label="$t('closeout_override_vpp_usage')"
                  :value="closeout.overriddenTotalAmount | formatNumber(2,2)"
              />
            </b-row>
            <b-row>
              <distribution-detail-field
                labelCols="6"
                valueCols="6"
                :label="$t('closeout_payments_calculated') | capitalize"
                :value="parseDate(closeout.snapshotCreated, $config.DATE_FORMAT)"
              />
            </b-row>
            <b-row>
              <distribution-detail-field
                labelCols="6"
                valueCols="6"
                :label="`${$t('closeout_transactions_created')}` | capitalize"
              >
                <template v-slot:value v-if="closeout.status">
                  <template v-if="transactionsCreated">
                    {{ $t("yes") | capitalize }}
                  </template>
                  <template v-else>
                    {{ $t("no") | capitalize }}
                  </template>
                </template>
              </distribution-detail-field>
            </b-row>
          </b-col>
        </labeled-wrapper>
      </div>
      <b-row class="fixed-footer mr-2">
        <b-col class="d-flex justify-content-end" style="padding: 0" v-if="permissions.pages[Pages.CloseoutsList].canWrite">
        <b-button
          v-if="!editMode && !transactionsCreated"
          class="btn-outline-red"
          @click="editMode = true"
        >
          {{ $t("edit") | capitalize }}
        </b-button>
        <b-button
          class="ml-3 btn-outline-red"
          :disabled="invalid | loading"
          @click="submit"
          v-if="editMode"
        >
          {{ $t("save") | capitalize }}
        </b-button>
        <b-button
          v-if="editMode"
          class="ml-3 btn-outline-dark"
          @click="cancel"
          >{{ $t("cancel") | capitalize }}</b-button
        >
        <b-button
          v-if="!closeout.snapshotCreated && !editMode"
          class="ml-3 btn-secondary"
          @click="deleteCloseout"
          >{{ $t("delete") | capitalize }}</b-button
          >
        </b-col>
      </b-row>
    </validation-observer>
    <b-row class="pt-2" v-if="permissions.pages[Pages.CloseoutsSnapshot].canWrite">
      <b-col class="d-flex align-items-center">
        <template v-if="pendingChangesCount > 0">
          <b-button
            class="mr-2 btn-outline-red"
            @click="saveChanges"
          >
            {{ $t("save_changes") | capitalize }}
          </b-button>
          <b-button
            class="mr-3 btn-outline-dark"
            @click="discardChanges"
          >
            {{ $t("discard_changes") | capitalize }}
          </b-button>
        </template>
        <b-col class="d-flex justify-content-end pr-0">
          <b-button
            class="btn-outline-red"
            :disabled="closeout.calculationRunning || !canExportSnapshot"
            @click="exportSnapshot"
          >
            {{ $t("export_snapshot") | capitalize }}
          </b-button>
          <b-button
            class="ml-3 btn-outline-red"
            :disabled="closeout.calculationRunning || !canModifySnapshot"
            @click="refreshSnapshot"
          >
            {{ $t("recalculate_snapshot") | capitalize }}
          </b-button>
          <b-button
            class="btn-outline-red"
            :disabled="closeout.calculationRunning || !canCreateSnapshot"
            @click="createSnapshot"
          >
            {{ $t("create_snapshot") | capitalize }}
          </b-button>
          <b-button
            class="ml-3 btn-secondary"
            :disabled="closeout.calculationRunning || !canModifySnapshot"
            @click="deleteSnapshot"
            >{{ $t("delete_snapshot") | capitalize }}</b-button
          >
          <template v-if="permissions.pages[Pages.CloseoutsList].canWrite">
            <b-button
              class="ml-3 btn-outline-red"
              :disabled="closeout.calculationRunning || noCreateTransactionsReason !== ''"
              @click="$bvModal.show('create-transactions-modal')"
              :title="noCreateTransactionsReason"
            >
              {{ $t("create_transactions") | capitalize }}
            </b-button>
            <b-button
              class="ml-3 btn-secondary"
              :disabled="closeout.calculationRunning || !canRemoveTransactions"
              @click="$bvModal.show('undo-transactions-modal')"
              >{{ $t("undo_transactions") | capitalize }}</b-button
            >
          </template>
          <b-button
            class="ml-3 btn-outline-red"
            :disabled="closeout.calculationRunning || !canToggleDistributable"
            @click="showDistributableModal"
            >{{ (isDistributable ? $t("mark_as_not_distributable") : $t("mark_as_distributable")) | capitalize }}</b-button
          >
        </b-col>
      </b-col>
    </b-row>
    <div v-if="closeout.calculationRunning" class="mt-4 d-flex align-items-center justify-content-center">
      <b-spinner variant="dark" small />
      <span class="ml-2 pt-1">{{ $t("snapshot_calculation_running") }}</span>
    </div>
    <div v-else-if="closeout.calculationFailed" class="mt-4 d-flex align-items-center justify-content-center">
      <fa-icon :icon="['fas', 'exclamation-circle']" class="text-red" />
      <span class="ml-2 pt-1 text-red">{{ $t("snapshot_calculation_failed") }}</span>
    </div>
    <closeout-snapshot-review-table
      v-if="!closeout.calculationRunning"
      ref="snapshotTable"
      class="mt-4"
      :closeout-id="closeoutId"
      :college="closeout.college"
      :transactions-created="transactionsCreated"
      :preview="isPreview"
      @pending-changes="onPendingChanges"
    />
    <modal
      id="cancel-modal"
      :cancel-title="$t('no_continue_editing') | capitalize"
      :ok-title="$t('yes_cancel_editing') | capitalize"
      @ok="realCancel()"
      @cancel="$bvModal.hide('cancel-modal')"
      modal-type="info"
      centered
      :custom-title="$t('edit_closeout') | capitalize"
    >
      {{ $t("cancel_edit_closeout") }}
    </modal>
    <modal
      id="delete-modal"
      :cancel-title="$t('no') | capitalize"
      :ok-title="$t('yes') | capitalize"
      @ok="realDelete()"
      @cancel="$bvModal.hide('delete-modal')"
      modal-type="info"
      centered
      :custom-title="$t('delete_closeout') | capitalize"
    >
      {{ $t("delete_closeout_warning") }}
    </modal>
    <modal
      id="confirm-modal"
      :cancel-title="$t('cancel') | capitalize"
      :ok-title="$t('confirm') | capitalize"
      @ok="realSubmit()"
      @cancel="$bvModal.hide('confirm-modal')"
      modal-type="info"
      size="lg"
      centered
      :custom-title="$t('edit_closeout') | capitalize"
    >
      {{ $t("confirm_edit_closeout") }}
    </modal>
    <modal
      id="create-transactions-modal"
      :cancel-title="$t('cancel') | capitalize"
      :ok-title="$t('confirm') | capitalize"
      @ok="realCreateTransactions()"
      @cancel="$bvModal.hide('create-transactions-modal')"
      modal-type="info"
      centered
      :custom-title="$t('create_transactions') | capitalize"
    >
      {{ $t("confirm_create_transactions") }}
    </modal>
    <modal
      id="undo-transactions-modal"
      :cancel-title="$t('cancel') | capitalize"
      :ok-title="$t('confirm') | capitalize"
      @ok="realUndoTransactions()"
      @cancel="$bvModal.hide('undo-transactions-modal')"
      modal-type="info"
      centered
      :custom-title="$t('undo_transactions') | capitalize"
    >
      {{ $t("confirm_undo_transactions") }}
    </modal>
    <modal
      id="create-snapshot-modal"
      :cancel-title="$t('cancel') | capitalize"
      :ok-title="$t('confirm') | capitalize"
      @ok="realCreateSnapshot()"
      @cancel="$bvModal.hide('create-snapshot-modal')"
      modal-type="info"
      centered
      :custom-title="$t('create_snapshot') | capitalize"
    >
      {{ $t("confirm_create_snapshot") }}
    </modal>
    <modal
      id="delete-snapshot-modal"
      :cancel-title="$t('no') | capitalize"
      :ok-title="$t('yes') | capitalize"
      @ok="realDeleteSnapshot()"
      @cancel="$bvModal.hide('delete-snapshot-modal')"
      modal-type="info"
      centered
      :custom-title="$t('delete_snapshot') | capitalize"
    >
      {{ $t("confirm_delete_snapshot") }}
    </modal>
    <modal
      id="save-snapshot-changes-modal"
      :cancel-title="$t('cancel') | capitalize"
      :ok-title="$t('confirm') | capitalize"
      @ok="realSaveChanges()"
      @cancel="$bvModal.hide('save-snapshot-changes-modal')"
      modal-type="info"
      centered
      :custom-title="$t('save') | capitalize"
    >
      <p>{{ $t("snapshot_changes_summary", { count: pendingChangesCount }) }}</p>
      <ul>
        <li
          v-for="(count, status) in pendingChangesByStatus"
          :key="status"
        >
          {{ $t("snapshot_status_will_be_updated", { count, status: getStatusLabel(status) }) }}
        </li>
        <li v-if="pendingCommentChangesCount > 0">
          {{ $t("snapshot_comment_will_be_updated", { count: pendingCommentChangesCount }) }}
        </li>
        <li v-if="pendingRepresentationChangesCount > 0">
          {{ $t("snapshot_representation_will_be_updated", { count: pendingRepresentationChangesCount }) }}
        </li>
      </ul>
    </modal>
    <modal
      id="mark-as-distributable-modal"
      :cancel-title="$t('cancel') | capitalize"
      :ok-title="$t('confirm') | capitalize"
      @ok="realMarkAsDistributable()"
      @cancel="$bvModal.hide('mark-as-distributable-modal')"
      modal-type="info"
      centered
      :custom-title="(isDistributable ? $t('mark_as_not_distributable') : $t('mark_as_distributable')) | capitalize"
    >
      {{ isDistributable ? $t("confirm_mark_as_not_distributable") : $t("confirm_mark_as_distributable") }}
    </modal>
    <modal
      id="refresh-snapshot-modal"
      :cancel-title="$t('cancel') | capitalize"
      :ok-title="$t('confirm') | capitalize"
      @ok="realRefreshSnapshot()"
      @cancel="$bvModal.hide('refresh-snapshot-modal')"
      modal-type="info"
      centered
      :custom-title="$t('recalculate_snapshot') | capitalize"
    >
      <b-form-checkbox
        v-model="refreshRepresentation"
        switch
      >
        {{ $t('refresh_representation') | capitalize }}
      </b-form-checkbox>
    </modal>
  </div>
</template>

<script>
import { ValidationObserver, extend } from "vee-validate"
import { closeoutReserveValueTypes, closeoutSnapshotStatusActionsLabels, closeoutStatusOptions } from "@/constants"
import { mapActions, mapMutations, mapState } from "vuex"
import CloseoutSnapshotReviewTable from "@/pages/Closeouts/CloseoutSnapshotReviewTable.vue"
import DistributionDetailField from "@/components/DistributionDetailField.vue"
import LabeledWrapper from "@/components/LabeledWrapper.vue"
import { Pages } from "@/utils/pages"
import SnapshotExportForm from "../Closeouts/SnapshotExportForm.vue"
import { capitalize } from "lodash"
import { distributionSectionMixin } from "@/utils/mixins"
import { i18n } from "@/utils/i18n"
import parseDate from "@/utils/date-parser"

extend("vppOverride", {
  params: ["vpp"],
  validate (value, { vpp }) {
    if (!vpp || !value) {
      return true
    }
    return value <= vpp
  },
  message: i18n.t("vpp_override_should_be_less_than_vpp"),
})

export default {
  name: "CloseoutDetailPage",
  components: {
    SnapshotExportForm,
    CloseoutSnapshotReviewTable,
    DistributionDetailField,
    LabeledWrapper,
    ValidationObserver
  },
  mixins: [distributionSectionMixin],
  props: {
    closeoutId: String,
  },
  data () {
    return {
      closeout: {},
      originalCloseout: {},
      distribution: {},
      editMode: false,
      loading: false,
      pendingChanges: [],
      pollingTimer: null,
      refreshRepresentation: true,
      reserveTypeOptions: [
        {
          text: "%",
          value: closeoutReserveValueTypes.PERCENT,
        },
        {
          text: "CAD",
          value: closeoutReserveValueTypes.MONEY,
        },
      ],
      Pages,
    }
  },
  methods: {
    ...mapMutations("alert", ["success", "error"]),
    ...mapActions("alert", ["success"]),
    parseDate,
    async getData () {
      try {
        const response = await this.$api.distributions.getCloseout(
          this.closeoutId,
        )
        this.closeout = response.data
        this.originalCloseout = JSON.parse(JSON.stringify(response.data))
        const distributionResponse =
          await this.$api.distributions.getDistribution(
            this.closeout.distributionId,
          )
        this.distribution = distributionResponse.data
        this.updatePolling()
      } catch (error) {
        this.error(`${capitalize(this.$t("error_occurred"))} \n ${JSON.stringify(error.response.data)}`)
      }
    },
    updatePolling () {
      if (this.closeout.calculationRunning) {
        if (!this.pollingTimer) {
          this.pollingTimer = setInterval(async () => {
            const response = await this.$api.distributions.getCloseout(this.closeoutId)
            this.closeout = response.data
            if (!this.closeout.calculationRunning) {
              clearInterval(this.pollingTimer)
              this.pollingTimer = null
            }
          }, 15000)
        }
      } else {
        clearInterval(this.pollingTimer)
        this.pollingTimer = null
      }
    },
    getReserveValue (value, type) {
      const formattedValue = this.$options.filters.formatNumber(value, 2, 2)
      return type === closeoutReserveValueTypes.PERCENT ? `${formattedValue}%` : `$${formattedValue}`
    },
    getSnapshotRowsStat (field) {
      const value = this.closeout.snapshotRowsStats?.[field]
      return value === null || value === undefined ? "-" : String(value)
    },
    getReserveAmountLabel ({ reserveNumber, type }) {
      const reserveNumberLabel = this.hasTwoReserves ? reserveNumber : ""
      const typeLabel = type === closeoutReserveValueTypes.PERCENT ? "%" : this.$t("amount")

      return capitalize(
        `${this.$t("reserve")} ${reserveNumberLabel} ${typeLabel}`,
      )
    },
    getReserveNotesLabel ({ reserveNumber }) {
      const reserveNumberLabel = this.hasTwoReserves ? reserveNumber : ""

      return capitalize(
        `${this.$t("reserve")} ${reserveNumberLabel} ${this.$t("notes")}`,
      )
    },
    getViewReserveValue ({ reserveNumber, type }) {
      const value = this.closeout[`reserve${reserveNumber}Value`]
      if (type === this.closeout[`reserve${reserveNumber}ValueType`]) {
        return this.getReserveValue(
          this.closeout[`reserve${reserveNumber}Value`],
          type,
        ) 
      }
      else {
        const baseValue = this.closeout.totalCloseoutRevenueOverride || this.closeout.totalCloseoutRevenue
        if (this.closeout[`reserve${reserveNumber}ValueType`] === closeoutReserveValueTypes.PERCENT) {
          return this.getReserveValue(
            (this.closeout[`reserve${reserveNumber}Value`] / 100) * baseValue,
            closeoutReserveValueTypes.MONEY,
          )
        }
        if (this.closeout[`reserve${reserveNumber}ValueType`] === closeoutReserveValueTypes.MONEY) {
          return this.getReserveValue(
            ((this.closeout[`reserve${reserveNumber}Value`] / baseValue) * 100).toFixed(2),
            closeoutReserveValueTypes.PERCENT,
          )
        }
      }
      

      return value
    },
    edit () {
      this.editMode = true
    },
    submit () {
      this.$bvModal.show("confirm-modal")
    },
    cancel () {
      this.$bvModal.show("cancel-modal")
    },
    realCancel () {
      this.editMode = false
      this.success(this.$t("tariff_period_edit_cancelled"))
      this.closeout = JSON.parse(JSON.stringify(this.originalCloseout))
    },
    exportSnapshot () {
      this.$bvModal.show("snapshot-export-modal")
    },
    async realSubmit () {
      this.loading = true
      const updatedFields = this.getCloseoutPatchObject()
      this.$api.distributions
        .updateCloseout(this.closeoutId, updatedFields)
        .then(() => {
          this.editMode = false
          this.getData()
          this.success(capitalize(this.$t("closeout_updated")))
        })
        .catch((error) => {
          const message = error.response.data.fieldErrors
            ? error.response.data.fieldErrors
              .map((fieldError) => `${fieldError.field}: ${fieldError.message}`)
              .join("\n")
            : JSON.stringify(error.response.data)
          this.error(`${capitalize(this.$t("error"))} \n ${message}`)
        })
        .finally(() => {
          this.loading = false
        })
    },
    deleteCloseout () {
      this.$bvModal.show("delete-modal")
    },
    realDelete () {
      this.$api.distributions
        .deleteCloseout(this.closeoutId)
        .then(() => {
          this.$router
            .push({
              path: "/distributions/",
            })
            .then(() => {
              this.success(capitalize(this.$t("closeout_deleted")))
            })
        })
        .catch((error) => {
          this.error(`${capitalize(this.$t("error_occurred"))} \n ${JSON.stringify(error.response.data)}`)
        })
    },
    realCreateTransactions () {
      this.$api.distributions
        .createCloseoutTransactions(this.closeoutId)
        .then(() => {
          this.getData()
          this.success(capitalize(this.$t("transactions_creation_started")))
        })
        .catch((error) => {
          const data = error.response.data
          const detail = typeof data.detail === "string" ? JSON.parse(data.detail).detail : null
          this.error(detail || `${capitalize(this.$t("error_occurred"))} \n ${JSON.stringify(data)}`)
        })
    },
    realUndoTransactions () {
      this.$api.distributions
        .undoCloseoutTransactions(this.closeoutId)
        .then(() => {
          this.getData()
          this.success(capitalize(this.$t("transactions_undo_started")))
        })
        .catch((error) => {
          const data = error.response.data
          const detail = typeof data.detail === "string" ? JSON.parse(data.detail).detail : null
          this.error(detail || `${capitalize(this.$t("error_occurred"))} \n ${JSON.stringify(data)}`)
        })
    },
    createSnapshot () {
      this.$bvModal.show("create-snapshot-modal")
    },
    realCreateSnapshot () {
      this.$api.distributions
        .createCloseoutSnapshot(this.closeoutId)
        .then(() => {
          this.getData()
          this.success(capitalize(this.$t("snapshot_created")))
        })
        .catch((error) => {
          const data = error.response.data
          const detail = typeof data.detail === "string" ? JSON.parse(data.detail).detail : null
          this.error(detail || `${capitalize(this.$t("error_occurred"))} \n ${JSON.stringify(data)}`)
        })
    },
    deleteSnapshot () {
      this.$bvModal.show("delete-snapshot-modal")
    },
    realDeleteSnapshot () {
      this.$api.distributions
        .deleteCloseoutSnapshot(this.closeoutId)
        .then(() => {
          this.getData()
          this.success(capitalize(this.$t("snapshot_deleted")))
        })
        .catch((error) => {
          const data = error.response.data
          const detail = typeof data.detail === "string" ? JSON.parse(data.detail).detail : null
          this.error(detail || `${capitalize(this.$t("error_occurred"))} \n ${JSON.stringify(data)}`)
        })
    },
    onPendingChanges (changes) {
      this.pendingChanges = changes
    },
    saveChanges () {
      this.$bvModal.show("save-snapshot-changes-modal")
    },
    discardChanges () {
      this.$refs.snapshotTable.pendingChanges = []
    },
    async realSaveChanges () {
      try {
        await Promise.all(
          this.pendingChanges.map(change =>
            this.$api.distributions.updateSnapshotData(
              this.closeoutId,
              change.snapshotId,
              {
                recording_proprietary_id: change.recordingProprietaryId,
                rights_holder_proprietary_id: change.rightsHolderProprietaryId,
                payee_id: change.payeeId,
                ...(change.status && { status: change.status }),
                ...(change.comment !== undefined && { comment: change.comment }),
                ...(change.representation && {
                  payees_closeout: change.representation.map(r => ({
                    id: r.payeeId,
                    name: r.name,
                    society_name: r.societyName,
                    split: r.split,
                  })),
                }),
              },
            )
          )
        )
        this.$refs.snapshotTable.pendingChanges = []
        this.$refs.snapshotTable.clearSelection()
        this.$refs.snapshotTable.$refs.reviewTable.getData()
        this.getData()
        this.success(capitalize(this.$t("snapshot_changes_saved")))
      } catch (error) {
        this.error(`${capitalize(this.$t("error_occurred"))} \n ${JSON.stringify(error.response.data)}`)
      }
    },
    getStatusLabel (status) {
      const i18nKey = closeoutSnapshotStatusActionsLabels[status]
      return i18nKey ? capitalize(this.$t(i18nKey)) : status
    },
    showDistributableModal () {
      this.$bvModal.show("mark-as-distributable-modal")
    },
    realMarkAsDistributable () {
      const newValue = !this.isDistributable
      this.$api.distributions
        .markCloseoutDistributable(this.closeoutId, newValue)
        .then(() => {
          // the status is only written once the Harmony job finishes, so refetch rather than
          // guessing it here
          this.getData()
          const key = newValue ? "mark_as_distributable" : "mark_as_not_distributable"
          this.success(capitalize(this.$t(key)))
        })
        .catch((error) => {
          const data = error.response.data
          const detail = typeof data.detail === "string" ? JSON.parse(data.detail).detail : null
          this.error(detail || `${capitalize(this.$t("error_occurred"))} \n ${JSON.stringify(data)}`)
        })
    },
    refreshSnapshot () {
      this.$bvModal.show("refresh-snapshot-modal")
    },
    realRefreshSnapshot () {
      this.$api.distributions
        .refreshCloseoutSnapshot(this.closeoutId, { refresh_representation: this.refreshRepresentation })
        .then(() => {
          this.getData()
          this.success(capitalize(this.$t("snapshot_refresh_started")))
        })
        .catch((error) => {
          const data = error.response.data
          const detail = typeof data.detail === "string" ? JSON.parse(data.detail).detail : null
          this.error(detail || `${capitalize(this.$t("error_occurred"))} \n ${JSON.stringify(data)}`)
        })
    },
    getCloseoutPatchObject () {
      const patchObject = {}
      patchObject.totalCloseoutRevenueOverride =
        this.closeout.totalCloseoutRevenueOverride
      patchObject["reserve_1_value"] = this.closeout.reserve1Value
      patchObject["reserve_1_value_type"] = this.closeout.reserve1ValueType
      patchObject["reserve_1_note"] = this.closeout.reserve1Note
      patchObject["reserve_2_value"] = this.closeout.reserve2Value
      patchObject["reserve_2_value_type"] = this.closeout.reserve2ValueType
      patchObject["reserve_2_note"] = this.closeout.reserve2Note
      patchObject.vppOverride = this.closeout.vppOverride

      return Object.fromEntries(
        Object.entries(patchObject).map(([key, value]) => [
          key,
          value === "" ? null : value,
        ]),
      )
    },
  },
  computed: {
    ...mapState("user", ["permissions"]),
    // transactions exist from TRANSACTIONS_CREATED onwards
    transactionsCreated () {
      return [
        closeoutStatusOptions.TRANSACTIONS_CREATED,
        closeoutStatusOptions.DISTRIBUTABLE,
        closeoutStatusOptions.COMPLETED,
      ].includes(this.closeout.status)
    },
    isDistributable () {
      return this.closeout.status === closeoutStatusOptions.DISTRIBUTABLE
    },
    isPreview () {
      return (
        this.closeout.status === closeoutStatusOptions.SNAPSHOT_CREATED
        || this.closeout.status === closeoutStatusOptions.TRANSACTIONS_CREATED
      )
    },
    // Each action below is available from exactly one closeout status - the one the previous
    // step produced - so undoing a step re-enables the button that produced it. Harmony rejects
    // anything else, these guards just keep the buttons honest.
    canCreateSnapshot () {
      return this.closeout.status === closeoutStatusOptions.CLOSEOUT_CREATED
    },
    // there is nothing to export until the snapshot has been calculated, and every status past
    // that still has one - deleting the snapshot is the only way back to CLOSEOUT_CREATED
    canExportSnapshot () {
      return [
        closeoutStatusOptions.SNAPSHOT_CREATED,
        closeoutStatusOptions.TRANSACTIONS_CREATED,
        closeoutStatusOptions.DISTRIBUTABLE,
        closeoutStatusOptions.COMPLETED,
      ].includes(this.closeout.status)
    },
    // deleting and recalculating a snapshot both stop being available once transactions exist
    canModifySnapshot () {
      return this.closeout.status === closeoutStatusOptions.SNAPSHOT_CREATED
    },
    noCreateTransactionsReason () {
      if (this.closeout.status !== closeoutStatusOptions.SNAPSHOT_CREATED) return this.$t("snapshot_must_be_created")
      if (!this.closeout.vppApplied) return this.$t("snapshot_refresh_required")
      const stats = this.closeout.snapshotRowsStats
      if (!stats || !stats.totalTransactions) return this.$t("snapshot_must_be_created")
      if (stats.reviewTransactions !== 0) {
        return this.$t("snapshot_in_review")
      }
      return ""
    },
    canRemoveTransactions () {
      return this.closeout.status === closeoutStatusOptions.TRANSACTIONS_CREATED
    },
    // both directions of the distributable toggle, since the button carries both
    canToggleDistributable () {
      return [
        closeoutStatusOptions.TRANSACTIONS_CREATED,
        closeoutStatusOptions.DISTRIBUTABLE,
      ].includes(this.closeout.status)
    },
    hasTwoReserves () {
      if (this.editMode) return true
      return this.closeout.reserve1Value && this.closeout.reserve2Value
    },
    pendingChangesCount () {
      return this.pendingChanges.length
    },
    pendingCommentChangesCount () {
      return this.pendingChanges.filter(c => c.comment !== undefined).length
    },
    pendingRepresentationChangesCount () {
      return this.pendingChanges.filter(c => c.representation !== undefined).length
    },
    pendingChangesByStatus () {
      return this.pendingChanges.reduce((acc, { status }) => {
        if (status) acc[status] = (acc[status] || 0) + 1
        return acc
      }, {})
    },
  },
  mounted () {
    this.getData()
  },
  beforeDestroy () {
    clearInterval(this.pollingTimer)
  },
}
</script>

<style scoped>
.closeout-sections > * {
  flex: 1 1 0;
  min-width: 0;
}

@media (max-width: 1400px) {
  .closeout-sections {
    flex-direction: column;
  }
}

::v-deep .closeout-sections .container {
  min-height: unset;
  padding: 0.25rem 0;
}

::v-deep textarea.form-control {
  height: 40px;
  width: 82%;
}

::v-deep input.form-control,
::v-deep .input-group {
  width: 82%;
}

::v-deep .closeout-sections .container .b-col-6 {
  padding-right: 0px;
}

::v-deep .input-group .form-control {
  width: 10px;
}
</style>
